/**
 * AssessmentCapture — Sprint 11 redesign.
 *
 * Layout 3-zonas:
 *   - Sticky top bar: identidad del assessment + ConnectionBadge + SaveBadge
 *   - Sidebar izquierdo (lg+): plantillas con ProgressRing + secciones de la activa
 *   - Main: SurveyRenderer rediseñado (hideSectionNav porque la sidebar la controla)
 *   - Sticky bottom: progress global + "Cerrar y firmar"
 *
 * Smart resume: al cargar el assessment, salta automáticamente a la
 * primera pregunta no-respondida.
 *
 * Lógica de save/sync/complete intacta — solo cambia el layout visual.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  type ScoreDomain,
  type SurveyTemplate,
  SurveyTemplateSchema,
  assessmentProgress,
  firstUnansweredInTemplate,
  progressPercent,
  sectionProgress,
  templateProgress,
} from '@assessos/shared';

import { type AssessmentDetail, type CompleteResult, assessmentsApi } from '../api/assessments.js';
import { photosApi } from '../api/photos.js';
import { surveysApi } from '../api/surveys.js';
import { useAuth } from '../auth/AuthContext.js';
import { useAppShell } from '../components/AppShell.js';
import { PortalTokensPanel } from '../components/PortalTokensPanel.js';
import { ReportsPanel } from '../components/ReportsPanel.js';
import { SurveyRenderer } from '../components/SurveyRenderer.js';
import { Badge, ConnectionBadge, ProgressBar, ProgressRing, ScoreBar } from '../components/ui.js';
import { Button } from '../components/ui/Button.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton.js';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs.js';
import {
  cacheTemplate,
  enqueueCompleteChange,
  enqueuePhoto,
  enqueueResponsesChange,
  loadAssessment,
  loadCachedTemplate,
  patchLocalResponses,
  saveAssessment,
} from '../offline/repository.js';
import { syncNow } from '../offline/sync.js';

type SaveState = 'idle' | 'dirty' | 'queued' | 'syncing' | 'synced' | 'error';

function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  useEffect(() => {
    function on(): void {
      setOnline(true);
    }
    function off(): void {
      setOnline(false);
    }
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

function useElapsed(startedAt: string | null): string {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!startedAt) return '—';
  const ms = now - new Date(startedAt).getTime();
  if (ms < 0) return '—';
  const totalMin = Math.floor(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
}

export function AssessmentCapturePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const online = useOnlineStatus();
  const { setBackTo, setBreadcrumb } = useAppShell();

  // ADR-040 D4/D5: CaptureTokensPanel puede pasar el id de la instancia
  // auditor a través de location.state para que la página active esa instancia
  // directamente al cargar (evita que el auditor aterrice en la instancia cliente).
  const locationStateInstanceId =
    (location.state as { activeInstanceId?: string } | null)?.activeInstanceId ?? null;

  const [assessment, setAssessment] = useState<AssessmentDetail | null>(null);
  const [schemas, setSchemas] = useState<Record<string, SurveyTemplate>>({});
  const [activeInstanceId, setActiveInstanceId] = useState<string | null>(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [responsesByInstance, setResponsesByInstance] = useState<
    Record<string, Record<string, unknown>>
  >({});
  // S15-C · Notas del auditor por instance → questionRef → texto.
  const [auditorNotesByInstance, setAuditorNotesByInstance] = useState<
    Record<string, Record<string, string>>
  >({});
  // S15-C · Fotos subidas en la sesión actual, indexadas por instance →
  // questionRef → array. Permite mostrar thumbnails sin recargar del API.
  // V1: solo sesión actual; reload pierde el state (las fotos siguen en
  // servidor). Mejora futura: hydrate desde /api/v1/photos.
  const [photosByInstance, setPhotosByInstance] = useState<
    Record<
      string,
      Record<string, { id: string; file_url: string; applicable_standards?: string[] }[]>
    >
  >({});
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [completing, setCompleting] = useState(false);
  const [completeResult, setCompleteResult] = useState<CompleteResult | null>(null);
  const [completionQueued, setCompletionQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSmartResumed, setHasSmartResumed] = useState(false);

  const elapsed = useElapsed(assessment?.started_at ?? null);

  // Back button + breadcrumb (BB-UX-01 + BB-UX-03): se actualiza cuando se carga el assessment.
  useEffect(() => {
    const siteId = assessment?.site_id;
    if (!siteId) return;
    setBackTo({ href: `/sites/${siteId}/assessments`, label: 'Histórico' });
    return () => setBackTo(null);
  }, [setBackTo, assessment?.site_id]);

  useEffect(() => {
    if (!assessment) return;
    const siteId = assessment.site_id;
    const dateLabel = assessment.started_at
      ? new Date(assessment.started_at).toLocaleDateString('es-DO')
      : (id?.slice(0, 8) ?? 'Levantamiento');
    setBreadcrumb([
      { label: 'Clientes', href: '/clients' },
      { label: 'Histórico', href: `/sites/${siteId}/assessments` },
      { label: dateLabel },
    ]);
    return () => setBreadcrumb([]);
  }, [setBreadcrumb, assessment, id]);

  // ── Hidratacion local-first ────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);

    async function hydrate(): Promise<void> {
      let current: AssessmentDetail | null = null;
      try {
        current = await loadAssessment(id!);
        if (current && !cancelled) {
          applyAssessment(current);
          await ensureSchemas(current);
        }
      } catch {
        // ignore local read errors
      }
      try {
        const fresh = await assessmentsApi.get(id!);
        if (cancelled) return;
        applyAssessment(fresh);
        await saveAssessment(fresh).catch(() => undefined);
        await ensureSchemas(fresh);
      } catch (err) {
        if (!current) {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : 'Error cargando assessment');
        }
      }
    }

    function applyAssessment(a: AssessmentDetail): void {
      setAssessment(a);
      const init: Record<string, Record<string, unknown>> = {};
      const notesInit: Record<string, Record<string, string>> = {};
      for (const inst of a.instances) {
        init[inst.id] = inst.responses ?? {};
        // ADR-040 D3: filtrar claves reservadas (__prefill, etc.) de auditorNotes
        // antes de hidratarlas en el estado. El motor de notas solo debe ver
        // entradas de usuario (ref→texto), no metadatos del sistema.
        const rawNotes = inst.auditor_notes ?? {};
        const userNotes: Record<string, string> = {};
        for (const [k, v] of Object.entries(rawNotes)) {
          if (!k.startsWith('__')) userNotes[k] = v;
        }
        notesInit[inst.id] = userNotes;
      }
      setResponsesByInstance(init);
      setAuditorNotesByInstance(notesInit);
      setActiveInstanceId((prev) => {
        if (prev !== null) return prev;
        // ADR-040 D5: si CaptureTokensPanel navigó con state.activeInstanceId
        // (instancia AUDITOR), priorizarla. Si no, usar la primera instancia.
        if (locationStateInstanceId && a.instances.some((i) => i.id === locationStateInstanceId)) {
          return locationStateInstanceId;
        }
        // Preferir la instancia in_progress (auditor) sobre la completed (cliente).
        const inProgress = a.instances.find((i) => i.status === 'in_progress');
        return (inProgress ?? a.instances[0])?.id ?? null;
      });
    }

    async function ensureSchemas(a: AssessmentDetail): Promise<void> {
      const loaded: Record<string, SurveyTemplate> = {};
      await Promise.all(
        a.instances.map(async (inst) => {
          if (!inst.template) return;
          const code = inst.template.code;
          const version = inst.template_version;
          let raw: unknown;
          try {
            raw = await loadCachedTemplate(code, version);
          } catch {
            raw = null;
          }
          if (!raw) {
            try {
              const detail = await surveysApi.get(code, version);
              raw = detail.schema;
              await cacheTemplate(code, version, detail.schema).catch(() => undefined);
            } catch {
              return;
            }
          }
          const v = SurveyTemplateSchema.safeParse(raw);
          if (v.success) loaded[inst.id] = v.data;
        }),
      );
      if (cancelled) return;
      setSchemas((prev) => ({ ...prev, ...loaded }));
    }

    void hydrate();
    void syncNow();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // ── Smart resume: salta a la primera pregunta sin responder ─────────
  useEffect(() => {
    if (hasSmartResumed) return;
    if (!activeInstanceId) return;
    const schema = schemas[activeInstanceId];
    const responses = responsesByInstance[activeInstanceId];
    if (!schema || !responses) return;
    const pos = firstUnansweredInTemplate(schema, responses);
    if (pos) {
      setActiveSectionIndex(pos.sectionIndex);
    }
    setHasSmartResumed(true);
  }, [activeInstanceId, schemas, responsesByInstance, hasSmartResumed]);

  // ── Auto-save local + enqueue debounced ────────────────────────────
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function scheduleSave(
    instanceId: string,
    payload: Record<string, unknown>,
    notes: Record<string, string>,
  ): void {
    setSaveState('dirty');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void doSave(instanceId, payload, notes);
    }, 1500);
  }

  async function doSave(
    instanceId: string,
    payload: Record<string, unknown>,
    notes: Record<string, string>,
  ): Promise<void> {
    if (!id) return;
    setSaveState('syncing');
    try {
      await patchLocalResponses(id, instanceId, payload);
      await enqueueResponsesChange({
        assessmentId: id,
        instanceId,
        responses: payload,
        auditorNotes: notes,
      });
      setSaveState('queued');
      void syncNow().then((r) => {
        if (r.ok) setSaveState('synced');
        else setSaveState('queued');
      });
    } catch (err) {
      setSaveState('error');
      setError(err instanceof Error ? err.message : 'Error guardando local');
    }
  }

  function handleResponsesChange(instanceId: string, next: Record<string, unknown>): void {
    setResponsesByInstance((prev) => ({ ...prev, [instanceId]: next }));
    scheduleSave(instanceId, next, auditorNotesByInstance[instanceId] ?? {});
  }

  // S15-C · Cambio de nota del auditor por pregunta.
  function handleAuditorNoteChange(instanceId: string, qid: string, note: string): void {
    setAuditorNotesByInstance((prev) => {
      const prevInstance = prev[instanceId] ?? {};
      const nextInstance = { ...prevInstance, [qid]: note };
      // Limpieza: si la nota queda vacía, la quitamos del map para no
      // serializar entries inútiles.
      if (note.trim().length === 0) delete nextInstance[qid];
      const next = { ...prev, [instanceId]: nextInstance };
      scheduleSave(instanceId, responsesByInstance[instanceId] ?? {}, nextInstance);
      return next;
    });
  }

  async function handleComplete(): Promise<void> {
    if (!id || !assessment) return;
    setError(null);
    setCompleting(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    try {
      for (const inst of assessment.instances) {
        const r = responsesByInstance[inst.id];
        if (r) {
          await patchLocalResponses(id, inst.id, r);
          await enqueueResponsesChange({
            assessmentId: id,
            instanceId: inst.id,
            responses: r,
            auditorNotes: auditorNotesByInstance[inst.id] ?? {},
          });
        }
      }
      await enqueueCompleteChange(id);
      const result = await syncNow();
      if (!result.ok) {
        setCompletionQueued(true);
        return;
      }
      try {
        const fresh = await assessmentsApi.get(id);
        await saveAssessment(fresh).catch(() => undefined);
        if (fresh.status === 'completed' && fresh.signature) {
          const synthetic: CompleteResult = {
            id: fresh.id,
            status: 'completed',
            completed_at: fresh.completed_at ?? new Date().toISOString(),
            signature: fresh.signature,
            scores: fresh.instances.map((i) => {
              const score = i.score as {
                weighted?: number;
                perDomain?: Record<string, number>;
              } | null;
              return {
                instanceId: i.id,
                weighted: score?.weighted ?? 0,
                perDomain: score?.perDomain ?? {},
              };
            }),
          };
          setCompleteResult(synthetic);
        } else {
          setCompletionQueued(true);
        }
      } catch {
        setCompletionQueued(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cerrando assessment');
    } finally {
      setCompleting(false);
    }
  }

  async function handlePhotoUpload(
    instanceId: string,
    questionRef: string,
    file: File,
  ): Promise<{ id: string; file_url: string; size_bytes: number }> {
    if (!id) throw new Error('assessment_sin_id');
    const item = await enqueuePhoto({
      assessmentId: id,
      instanceId,
      questionRef,
      file,
      contentType: file.type || 'application/octet-stream',
    });
    void syncNow();
    const result = {
      id: item.id,
      file_url: `idb://photo/${item.id}`,
      size_bytes: file.size,
    };
    // S15-C · Reflejar la foto en el state del renderer para que el panel
    // de evidencia muestre el thumbnail inmediatamente.
    setPhotosByInstance((prev) => {
      const instMap = prev[instanceId] ?? {};
      const list = instMap[questionRef] ?? [];
      return {
        ...prev,
        [instanceId]: {
          ...instMap,
          [questionRef]: [...list, { id: item.id, file_url: result.file_url }],
        },
      };
    });
    return result;
  }

  // S20-B · PATCH applicable_standards de una foto via API. Solo actualiza
  // el server-side; el state local se actualiza si la llamada tuvo exito.
  // Nota: solo aplica a fotos ya sincronizadas al backend (id es UUID
  // server-side). Las fotos que aun viven en la cola IndexedDB no son
  // editables hasta que sync las suba.
  async function handlePatchPhotoStandards(
    instanceId: string,
    photoId: string,
    standards: string[],
  ): Promise<void> {
    const result = await photosApi.patchApplicableStandards(photoId, standards);
    setPhotosByInstance((prev) => {
      const instMap = prev[instanceId];
      if (!instMap) return prev;
      const nextInstMap = { ...instMap };
      for (const [qref, list] of Object.entries(instMap)) {
        const idx = list.findIndex((p) => p.id === photoId);
        if (idx === -1) continue;
        const updated = [...list];
        updated[idx] = { ...list[idx]!, applicable_standards: result.photo.applicable_standards };
        nextInstMap[qref] = updated;
        break;
      }
      return { ...prev, [instanceId]: nextInstMap };
    });
  }

  const activeInstance = useMemo(
    () => assessment?.instances.find((i) => i.id === activeInstanceId) ?? null,
    [assessment, activeInstanceId],
  );

  /**
   * ADR-040 D5 · Detección del patrón dual-instancia promovida:
   *   - Una instancia CLIENTE (completed, evidencia inmutable).
   *   - Una instancia AUDITOR (in_progress, editable, con prefill opcional).
   * Solo se activa cuando hay exactamente estas dos instancias; con más
   * instancias (assessment multi-survey normal) se usa el flujo actual.
   */
  const dualInstance = useMemo(() => {
    if (assessment?.instances.length !== 2) return null;
    const clientInst = assessment.instances.find((i) => i.status === 'completed');
    const auditorInst = assessment.instances.find((i) => i.status === 'in_progress');
    if (!clientInst || !auditorInst) return null;
    // GG-001: el modo dual SOLO aplica a assessments promovidos desde captura, marcados
    // con la clave de sistema `__prefill` en la instancia auditor. Sin esa marca, un
    // assessment multi-survey normal con 2 instancias NO debe activar la UI dual ni
    // tratar la instancia completed como "evidencia del cliente".
    const notes = auditorInst.auditor_notes as Record<string, unknown> | undefined;
    if (!notes || !('__prefill' in notes)) return null;
    return { clientInst, auditorInst };
  }, [assessment]);

  /**
   * ADR-040 D3/D5 · Refs pre-llenados en la instancia AUDITOR.
   * Se leen de `auditorNotes.__prefill.refs` (clave de sistema, nunca
   * expuesta como nota de usuario porque filtramos prefijo `__` al hidratar).
   * Aquí accedemos al JSON crudo de la instancia (no al state de notas de usuario).
   */
  const auditorPrefillRefs = useMemo((): string[] => {
    if (!dualInstance) return [];
    const rawNotes = dualInstance.auditorInst.auditor_notes as Record<string, unknown> | undefined;
    if (!rawNotes) return [];
    const prefillMeta = rawNotes.__prefill as { refs?: string[] } | undefined;
    return prefillMeta?.refs ?? [];
  }, [dualInstance]);

  const globalProgress = useMemo(() => {
    if (!assessment) return { answered: 0, total: 0, required: 0, requiredAnswered: 0 };
    const items = assessment.instances
      .map((inst) => {
        const schema = schemas[inst.id];
        const responses = responsesByInstance[inst.id] ?? {};
        if (!schema) return null;
        return { template: schema, responses };
      })
      .filter(
        (x): x is { template: SurveyTemplate; responses: Record<string, unknown> } => x !== null,
      );
    return assessmentProgress(items);
  }, [assessment, schemas, responsesByInstance]);

  if (error && !assessment) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-6">
        {/* SH-2: E1 EmptyState variant="error" reemplaza bloque rosa inline */}
        <EmptyState
          variant="error"
          title="No se pudo cargar el levantamiento"
          description={error}
        />
      </main>
    );
  }
  if (!assessment) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-6">
        {/* SH-2: E1 LoadingSkeleton variant="card" reemplaza texto ad-hoc */}
        <LoadingSkeleton variant="card" rows={4} label="Cargando levantamiento…" />
      </main>
    );
  }

  if (completeResult) {
    // Construye mapa instanceId → ScoreDomain[] para mostrar nombres legibles en CompleteSummary.
    const domainsByInstance: Record<string, ScoreDomain[]> = Object.fromEntries(
      Object.entries(schemas).map(([instId, tpl]) => [instId, tpl.scoring.domains]),
    );
    return (
      <CompleteSummary
        result={completeResult}
        assessmentId={assessment.id}
        domainsByInstance={domainsByInstance}
        onBack={() => navigate('/clients')}
      />
    );
  }

  if (completionQueued) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-12 space-y-6">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center space-y-3">
          <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-7 w-7"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" strokeLinecap="round" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-amber-900">Cierre encolado para sincronizar</h1>
          <p className="text-sm text-amber-800 leading-relaxed max-w-md mx-auto">
            Estás offline o el server no respondió. El cierre se aplicará automáticamente cuando
            haya conectividad. Tu firma queda guardada localmente.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/clients')}
          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          ← Volver al inicio
        </button>
      </main>
    );
  }

  const isCompleted = assessment.status === 'completed';
  const globalPct = progressPercent(globalProgress);
  const requiredRemaining = globalProgress.required - globalProgress.requiredAnswered;
  const canComplete = requiredRemaining === 0 && globalProgress.total > 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* ── Sticky top bar ────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Volver"
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-slate-600 hover:bg-slate-50"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-4 w-4"
              >
                <polyline points="12 4 6 10 12 16" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-700">
                LEVANTAMIENTO · {assessment.status.toUpperCase()}
              </p>
              <h1 className="text-base font-semibold text-brand-900 leading-tight truncate">
                <span className="font-mono">{assessment.id.slice(0, 8)}</span>
                {assessment.notes && (
                  <span className="ml-2 text-slate-500 font-normal">· {assessment.notes}</span>
                )}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden md:inline-flex items-center gap-1.5 text-xs text-slate-500">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="h-3.5 w-3.5"
              >
                <circle cx="8" cy="8" r="6" />
                <path d="M8 4v4l3 2" strokeLinecap="round" />
              </svg>
              {elapsed}
            </span>
            <button
              type="button"
              onClick={() => navigate(`/assessments/${assessment.id}/meetings`)}
              className="hidden md:inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              title="ISO 19011 §6.4 — actas de apertura y cierre"
            >
              📝 Actas
            </button>
            {assessment.is_integrated && (
              <button
                type="button"
                onClick={() => navigate(`/assessments/${assessment.id}/iso-matrix`)}
                className="hidden md:inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                title="IMS — Matriz Annex SL"
              >
                🧩 Matriz
              </button>
            )}
            {session?.user?.email && (
              <span className="hidden lg:inline text-xs text-slate-500">{session.user.email}</span>
            )}
            <ConnectionBadge online={online} />
            <SaveBadge state={saveState} />
          </div>
        </div>
      </header>

      {error && (
        <div className="mx-auto max-w-7xl px-4 pt-4">
          <p
            role="alert"
            className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700"
          >
            {error}
          </p>
        </div>
      )}

      {/* ── Strip de progreso de sección (md: 768–1023 px) ──────────── */}
      {/* Solo visible en tablet, reemplaza la función de la sidebar lateral. */}
      {/* lg:hidden: en desktop la sidebar cumple esta función. WCAG 2.4.6 h2. */}
      {activeInstance &&
        schemas[activeInstance.id] &&
        (() => {
          const schema = schemas[activeInstance.id]!;
          const section = schema.sections[activeSectionIndex];
          const responses = responsesByInstance[activeInstance.id] ?? {};
          const p = section ? sectionProgress(section, responses) : null;
          const isFirst = activeSectionIndex === 0;
          const isLast = activeSectionIndex >= schema.sections.length - 1;
          return (
            <div className="md:block lg:hidden border-b border-slate-200 bg-white px-4 py-2">
              <div className="mx-auto max-w-7xl flex items-center gap-2">
                {/* Chevron anterior — Button ghost min-h-[44px] por base CVA */}
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isFirst}
                  onClick={() => setActiveSectionIndex((i) => Math.max(0, i - 1))}
                  aria-label="Sección anterior"
                  className="shrink-0"
                >
                  ←
                </Button>
                <div className="flex-1 min-w-0 text-center">
                  {/* h2 semántico: WCAG 2.4.6 — jerarquía h1 (top bar) → h2 (sección) */}
                  <h2 className="text-sm font-semibold text-cumple-primary-900 truncate">
                    {section?.title ?? 'Sección'}
                  </h2>
                  {/* aria-live: anuncia progreso al responder — WCAG 4.1.3 */}
                  <p
                    aria-live="polite"
                    aria-atomic="true"
                    aria-label={
                      p ? `${p.answered} de ${p.total} respondidas en esta sección` : undefined
                    }
                    className="text-xs text-cumple-gray-700 mt-0.5"
                  >
                    {p ? `${p.answered} / ${p.total} respondidas` : ''}
                  </p>
                </div>
                {/* Chevron siguiente */}
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isLast}
                  onClick={() =>
                    setActiveSectionIndex((i) => Math.min(schema.sections.length - 1, i + 1))
                  }
                  aria-label="Sección siguiente"
                  className="shrink-0"
                >
                  →
                </Button>
              </div>
            </div>
          );
        })()}

      {/* ── Body con sidebar + main ─────────────────────────────────── */}
      <div className="mx-auto max-w-7xl px-4 py-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* SIDEBAR */}
        <aside className="space-y-4 lg:sticky lg:top-[88px] lg:self-start lg:max-h-[calc(100vh-180px)] lg:overflow-y-auto">
          {/* ADR-040 D5: Patrón dual-instancia → selector de tabs Auditor/Cliente.
              Fuera del patrón → lista normal de plantillas. */}
          {dualInstance ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                Perspectiva
              </h2>
              <Tabs
                value={activeInstanceId ?? dualInstance.auditorInst.id}
                onValueChange={(val) => {
                  setActiveInstanceId(val);
                  setActiveSectionIndex(0);
                }}
              >
                <TabsList variant="pills" className="w-full">
                  <TabsTrigger value={dualInstance.auditorInst.id} className="flex-1 text-xs">
                    Auditor
                  </TabsTrigger>
                  <TabsTrigger value={dualInstance.clientInst.id} className="flex-1 text-xs">
                    Cliente (evidencia)
                  </TabsTrigger>
                </TabsList>
                <TabsContent value={dualInstance.auditorInst.id}>
                  {(() => {
                    const schema = schemas[dualInstance.auditorInst.id];
                    const responses = responsesByInstance[dualInstance.auditorInst.id] ?? {};
                    const tp = schema ? templateProgress(schema, responses) : null;
                    const pct = tp ? progressPercent(tp) : 0;
                    return tp ? (
                      <div className="space-y-1 pt-1">
                        <ProgressBar
                          percent={pct}
                          size="sm"
                          tone={pct === 100 ? 'emerald' : 'brand'}
                        />
                        <p className="text-[10px] text-slate-500">
                          {tp.answered}/{tp.total} respondidas · {tp.requiredAnswered}/{tp.required}{' '}
                          requeridas
                        </p>
                        {auditorPrefillRefs.length > 0 && (
                          <p className="text-[10px] text-blue-700">
                            {auditorPrefillRefs.length} campos pre-llenados del cliente
                          </p>
                        )}
                      </div>
                    ) : null;
                  })()}
                </TabsContent>
                <TabsContent value={dualInstance.clientInst.id}>
                  <p className="text-[10px] text-slate-500 pt-1">
                    Insumo del cliente — solo lectura
                  </p>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                Plantillas ({assessment.instances.length})
              </h2>
              <ul className="space-y-2">
                {assessment.instances.map((inst) => {
                  const schema = schemas[inst.id];
                  const responses = responsesByInstance[inst.id] ?? {};
                  const tp = schema ? templateProgress(schema, responses) : null;
                  const pct = tp ? progressPercent(tp) : 0;
                  const active = inst.id === activeInstanceId;
                  return (
                    <li key={inst.id}>
                      {/* min-h-[44px] vía py-2.5 + contenido: área táctil WCAG 2.5.5 */}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveInstanceId(inst.id);
                          setActiveSectionIndex(0);
                        }}
                        className={
                          'w-full text-left rounded-xl border-2 px-3 py-2.5 min-h-[44px] transition-colors ' +
                          (active
                            ? 'border-cumple-primary-700 bg-cumple-primary-50'
                            : 'border-slate-200 bg-white hover:border-slate-300')
                        }
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs font-semibold text-brand-900 truncate">
                            {(inst.template?.code ?? 'SURVEY').toUpperCase()} v
                            {inst.template_version}
                          </span>
                          <span className="text-xs font-bold text-brand-700">{pct}%</span>
                        </div>
                        {tp && (
                          <>
                            <ProgressBar
                              percent={pct}
                              size="sm"
                              tone={pct === 100 ? 'emerald' : 'brand'}
                            />
                            <p className="mt-1.5 text-[10px] text-slate-500">
                              {tp.answered}/{tp.total} respondidas · {tp.requiredAnswered}/
                              {tp.required} requeridas
                            </p>
                          </>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Sidebar secundario: secciones de la plantilla activa */}
          {activeInstance && schemas[activeInstance.id] && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 mb-2">
                Secciones
              </h2>
              <ul className="space-y-1">
                {schemas[activeInstance.id]!.sections.map((sec, idx) => {
                  const responses = responsesByInstance[activeInstance.id] ?? {};
                  const p = sectionProgress(sec, responses);
                  const pct = progressPercent(p);
                  const active = idx === activeSectionIndex;
                  const complete = pct === 100;
                  return (
                    <li key={sec.id}>
                      {/* min-h-[44px]: área táctil WCAG 2.5.5 */}
                      <button
                        type="button"
                        onClick={() => setActiveSectionIndex(idx)}
                        className={
                          'w-full text-left rounded-lg px-3 py-2 min-h-[44px] transition-colors flex items-center gap-2 ' +
                          (active
                            ? 'bg-cumple-primary-50 text-cumple-primary-900'
                            : 'text-slate-600 hover:bg-slate-50')
                        }
                      >
                        <span
                          className={
                            'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ' +
                            (complete
                              ? 'bg-emerald-500 text-white'
                              : active
                                ? 'bg-brand-700 text-white'
                                : 'bg-slate-100 text-slate-500')
                          }
                        >
                          {complete ? '✓' : idx + 1}
                        </span>
                        <span className="flex-1 truncate text-sm font-medium">{sec.title}</span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {p.answered}/{p.total}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </aside>

        {/* MAIN */}
        <main>
          {activeInstance && schemas[activeInstance.id] ? (
            <>
              {/* ADR-040 D5: Banner informativo cuando se muestra la instancia cliente (evidencia). */}
              {activeInstance.id === dualInstance?.clientInst.id && (
                <div
                  role="note"
                  className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
                >
                  <strong className="font-semibold">Insumo del cliente — solo lectura.</strong> Esta
                  es la información que el cliente proporcionó. Úsala como referencia; edita en la
                  pestaña "Auditor".
                </div>
              )}
              <SurveyRenderer
                schema={schemas[activeInstance.id]!}
                responses={responsesByInstance[activeInstance.id] ?? {}}
                onChange={(next) => handleResponsesChange(activeInstance.id, next)}
                onUploadPhoto={async (qref, file) =>
                  handlePhotoUpload(activeInstance.id, qref, file)
                }
                // La instancia cliente en patrón dual es siempre read-only (evidencia inmutable).
                readOnly={isCompleted || activeInstance.id === dualInstance?.clientInst.id}
                activeSectionIndex={activeSectionIndex}
                onSectionChange={setActiveSectionIndex}
                hideSectionNav
                auditorNotes={auditorNotesByInstance[activeInstance.id] ?? {}}
                onAuditorNoteChange={(qid, note) =>
                  handleAuditorNoteChange(activeInstance.id, qid, note)
                }
                photosByQuestion={photosByInstance[activeInstance.id] ?? {}}
                onPatchPhotoStandards={(photoId, standards) =>
                  handlePatchPhotoStandards(activeInstance.id, photoId, standards)
                }
                // ADR-040 D5: pasa refs pre-llenados solo en la instancia auditor.
                // Spread condicional para no pasar `undefined` explícito con exactOptionalPropertyTypes.
                {...(activeInstance.id === dualInstance?.auditorInst.id &&
                auditorPrefillRefs.length > 0
                  ? { prefillRefs: auditorPrefillRefs }
                  : {})}
              />
            </>
          ) : (
            /* SH-2: E1 LoadingSkeleton variant="block" reemplaza texto ad-hoc "Cargando schema..." */
            <LoadingSkeleton variant="block" rows={3} label="Cargando estructura del formulario…" />
          )}
        </main>
      </div>

      {/* ── Sticky bottom: progress global + acciones ──────────────── */}
      {/* ADR-040 D5: ocultar footer de acciones en la pestaña cliente (read-only). */}
      {!isCompleted && !(activeInstanceId === dualInstance?.clientInst.id) && (
        <footer className="fixed bottom-0 inset-x-0 z-30 border-t border-slate-200 bg-white shadow-[0_-8px_24px_-12px_rgba(12,27,77,0.12)]">
          <div className="mx-auto max-w-7xl px-4 py-3 flex items-center gap-4">
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="flex items-baseline justify-between gap-3 text-xs">
                <span className="font-semibold text-cumple-primary-900">Progreso global</span>
                {/* aria-live anuncia cambios al responder preguntas — WCAG 4.1.3 */}
                <span
                  aria-live="polite"
                  aria-atomic="true"
                  aria-label={`${globalProgress.answered} de ${globalProgress.total} controles completados`}
                  className="font-bold text-cumple-primary-700"
                >
                  {globalProgress.answered}/{globalProgress.total} · {globalPct}%
                </span>
              </div>
              <ProgressBar percent={globalPct} tone={globalPct === 100 ? 'emerald' : 'brand'} />
              {!canComplete && requiredRemaining > 0 && (
                <p id="pending-required-count" className="text-[11px] text-amber-700 font-medium">
                  {requiredRemaining} preguntas requeridas pendientes antes de cerrar
                </p>
              )}
            </div>
            {/* CTA principal E1 — área táctil garantizada min-h-[44px] por Button base CVA.
                H3.1: en secciones intermedias navega a la siguiente sección (puramente
                presentacional); en la última sección ejecuta el cierre/firma. */}
            {activeInstance &&
            schemas[activeInstance.id] &&
            activeSectionIndex < schemas[activeInstance.id]!.sections.length - 1 ? (
              <Button
                variant="primary"
                size="md"
                className="shrink-0"
                onClick={() => setActiveSectionIndex((i) => i + 1)}
              >
                Guardar y siguiente →
              </Button>
            ) : (
              <Button
                variant="primary"
                size="md"
                disabled={completing || !canComplete}
                aria-disabled={completing || !canComplete}
                aria-describedby={
                  !canComplete && requiredRemaining > 0 ? 'pending-required-count' : undefined
                }
                loading={completing}
                loadingText="Cerrando…"
                onClick={() => {
                  void handleComplete();
                }}
                className="shrink-0"
              >
                {completing ? 'Cerrando…' : 'Cerrar y firmar →'}
              </Button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}

function SaveBadge({ state }: { state: SaveState }): JSX.Element {
  const map: Record<SaveState, { text: string; cls: string } | null> = {
    idle: null,
    dirty: { text: 'Cambios sin guardar', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    syncing: { text: 'Guardando…', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    queued: { text: 'En cola', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    synced: { text: 'Guardado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    error: { text: 'Error', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  };
  const info = map[state];
  if (!info) return <span />;
  return (
    /* role="status" + aria-live anuncia transiciones de estado a AT — WCAG 4.1.3 */
    <span
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={`hidden sm:inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${info.cls}`}
    >
      {info.text}
    </span>
  );
}

/**
 * Pantalla de resumen post-cierre (S11-E). Muestra ProgressRing del score
 * global por instancia, ScoreBars por dominio, top hallazgos (futuro) y
 * acciones de descarga + magic-link.
 */
function CompleteSummary({
  result,
  assessmentId,
  domainsByInstance = {},
  onBack,
}: {
  result: CompleteResult;
  assessmentId: string;
  /**
   * Mapa instanceId → dominios del template, para mostrar el nombre legible
   * en lugar del id raw (p.ej. "identify" → "Gobierno + procedimientos + trazabilidad").
   * Opcional: si no se pasa, se usa el id como fallback.
   *
   * TODO(deuda-tecnica): el catálogo declara dominios NIST por defecto en 65 plantillas;
   * re-catalogación pendiente Sprint Y con GRC.
   */
  domainsByInstance?: Record<string, ScoreDomain[]>;
  onBack: () => void;
}): JSX.Element {
  const aggregateScore =
    result.scores.length > 0
      ? result.scores.reduce((s, x) => s + x.weighted, 0) / result.scores.length
      : 0;

  return (
    <main className="mx-auto max-w-5xl px-4 py-10 space-y-6">
      {/* Hero post-cierre */}
      <section className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white p-8">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1 space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
              <svg viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
                <path
                  d="M14 4L6 12 2 8"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              ASSESSMENT CERRADO Y FIRMADO
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-brand-900 tracking-tight">
              Tu reporte está listo
            </h1>
            <p className="text-sm text-slate-600 leading-relaxed max-w-lg">
              Firmado el {new Date(result.completed_at).toLocaleString('es-DO')}. Score global
              compuesto en{' '}
              <strong className="text-emerald-700">{aggregateScore.toFixed(1)} / 100</strong>.
            </p>
            <p className="font-mono text-[10px] text-slate-400">
              hash: {result.signature.hash.slice(0, 24)}…
            </p>
          </div>
          <div className="flex justify-center">
            <ProgressRing
              percent={Math.round(aggregateScore)}
              size={140}
              stroke={12}
              label="score"
              tone={aggregateScore >= 75 ? 'emerald' : aggregateScore >= 50 ? 'amber' : 'rose'}
            />
          </div>
        </div>
      </section>

      {/* Reports + Portal */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <ReportsPanel assessmentId={assessmentId} />
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <PortalTokensPanel assessmentId={assessmentId} />
        </div>
      </div>

      {/* Scores por instancia */}
      {result.scores.map((s, idx) => (
        <section
          key={s.instanceId}
          className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5"
        >
          <header className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-brand-900">
                Plantilla {idx + 1} · Score por dominio
              </h2>
              <p className="text-xs text-slate-500">
                {Object.keys(s.perDomain).length} dominios evaluados
              </p>
            </div>
            <Badge tone={s.weighted >= 75 ? 'emerald' : s.weighted >= 50 ? 'amber' : 'rose'}>
              {s.weighted.toFixed(1)} / 100
            </Badge>
          </header>
          <div className="space-y-3">
            {Object.entries(s.perDomain).map(([d, v]) => {
              // Resuelve el nombre legible del dominio desde el template.
              // Fallback: capitalizar el id y reemplazar _ por espacio.
              const domains = domainsByInstance[s.instanceId] ?? [];
              const domainName =
                domains.find((x) => x.id === d)?.name ??
                d.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
              return <ScoreBar key={d} label={domainName} score={v} />;
            })}
          </div>
        </section>
      ))}

      <button
        type="button"
        onClick={onBack}
        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
      >
        ← Volver al inicio
      </button>
    </main>
  );
}
